import React, { useEffect, useMemo, useState } from "react";
import { exportOrder } from "@/lib/exportOrder";
import { api } from "@/lib/api";
import { useTableSort } from "@/lib/tableSort";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Download, FileText, Search, Plane } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { isGeneralManager } from "@/lib/roles";

const NAVY = "#0F2C59";
const PRED_CLS = {
  KESIN: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  BELKI: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  KARSI: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300",
  GELMEYECEK: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
  ULASILAMADI: "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300",
};

export const BranchVoters = ({ branch = "all", onBranchChange }) => {
  const { user } = useAuth();
  const gm = isGeneralManager(user);
  const [data, setData] = useState(null);
  const [travel, setTravel] = useState("all");
  const [committee, setCommittee] = useState("all");
  const [status, setStatus] = useState(gm ? "all" : "faal");
  const [prediction, setPrediction] = useState("all");
  const [responsible, setResponsible] = useState("all");
  const [proxy, setProxy] = useState("all");
  const [conflict, setConflict] = useState("all");
  const [q, setQ] = useState("");
  const [exporting, setExporting] = useState(false);
  const [saving, setSaving] = useState("");

  const filterParams = {
    branch: branch !== "all" ? branch : undefined,
    travel,
    committee_no: committee !== "all" ? committee : undefined,
    status: status !== "all" ? status : undefined,
    prediction: prediction !== "all" ? prediction : undefined,
    responsible: responsible !== "all" ? responsible : undefined,
    proxy: proxy !== "all" ? proxy : undefined,
    conflict: conflict !== "all" ? conflict : undefined,
  };

  const load = () => {
    setData(null);
    api.get("/dashboard/branch-voters", { params: filterParams })
      .then(({ data }) => setData(data))
      .catch(() => { setData({ rows: [], days: [] }); toast.error("Şube seçmenleri yüklenemedi"); });
  };
  useEffect(load, [branch, travel, committee, status, prediction, responsible, proxy, conflict]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => {
    const term = q.trim().toLocaleLowerCase("tr");
    if (!term) return data?.rows || [];
    return (data?.rows || []).filter((r) =>
      (r.company_name || "").toLocaleLowerCase("tr").includes(term)
      || (r.eff_responsible || "").toLocaleLowerCase("tr").includes(term)
      || (r.voting_person || "").toLocaleLowerCase("tr").includes(term)
      || String(r.sicil_no || "").includes(term));
  }, [data, q]);
  const bvT = useTableSort(rows);

  const saveDate = async (r, field, value) => {
    setSaving(r.id + field);
    try {
      await api.patch(`/voters/${r.id}`, value ? { [field]: value } : { clear_fields: [field] });
      setData((d) => ({ ...d, rows: d.rows.map((x) => (x.id === r.id ? { ...x, [field]: value } : x)) }));
      toast.success("Kaydedildi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally { setSaving(""); }
  };

  const doExport = async (fmt) => {
    setExporting(true);
    try {
      const params = { ...filterParams, format: fmt, only: 1,
        order: await exportOrder(bvT.sorted.map((r) => r.id)) };
      const res = await api.get("/dashboard/branch-voters/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sube_secmenleri.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch {
      toast.error("İndirilemedi");
    } finally { setExporting(false); }
  };

  if (!data) return <Skeleton className="h-96 w-full" />;

  return (
    <div className="space-y-3" data-testid="branch-voters">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-3" data-testid="bv-total">
          <div className="text-xl font-bold tabular-nums" style={{ color: NAVY }}>{data.total}</div>
          <div className="text-xs text-muted-foreground">Şube firması</div>
        </Card>
        <Card className="p-3" data-testid="bv-faal">
          <div className="text-xl font-bold tabular-nums" style={{ color: NAVY }}>{data.faal}</div>
          <div className="text-xs text-muted-foreground">FAAL firma</div>
        </Card>
        <Card className="p-3" data-testid="bv-planned">
          <div className="text-xl font-bold tabular-nums text-emerald-600">{data.planned}</div>
          <div className="text-xs text-muted-foreground">Ulaşım tarihi girilmiş</div>
        </Card>
        <Card className="p-3" data-testid="bv-days">
          <div className="text-xl font-bold tabular-nums text-violet-600">{(data.days || []).length}</div>
          <div className="text-xs text-muted-foreground">Planlı gün</div>
        </Card>
      </div>

      {data.preds && (
        <Card className="p-3" data-testid="bv-pred-summary">
          <p className="text-xs font-medium mb-2">Şube gözünden 2026 tahmin dağılımı (FAAL üyeler)</p>
          <div className="flex flex-wrap gap-2">
            {[["KESIN", "KESİN"], ["BELKI", "BELKİ"], ["GELMEYECEK", "GELMEYECEK"],
              ["ULASILAMADI", "ULAŞILAMADI"], ["KARSI", "KARŞI"], ["BOS", "İşaretsiz"]].map(([k, label]) => (
              <span key={k} className={`text-[11px] font-medium px-2.5 py-1 rounded-full ${PRED_CLS[k] || "bg-muted text-muted-foreground"}`} data-testid={`bv-pred-${k}`}>
                {label}: <b className="tabular-nums">{data.preds[k] ?? 0}</b>
              </span>
            ))}
          </div>
        </Card>
      )}

      {(data.days || []).length > 0 && (
        <Card className="p-3" data-testid="bv-day-summary">
          <p className="text-xs font-medium mb-2 flex items-center gap-1.5"><Plane className="h-3.5 w-3.5" /> Gün gün ulaşım yoğunluğu</p>
          <div className="flex flex-wrap gap-2">
            {data.days.map((d) => (
              <span key={d.date} className="text-[11px] px-2 py-1 rounded-md border border-border" data-testid={`bv-day-${d.date}`}>
                <b>{d.date.slice(8)}.{d.date.slice(5, 7)}</b>
                {d.arrivals > 0 && <span className="text-emerald-600"> ↓{d.arrivals} geliş</span>}
                {d.returns > 0 && <span className="text-sky-600"> ↑{d.returns} dönüş</span>}
              </span>
            ))}
          </div>
        </Card>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {data.can_select && (
          <Select value={branch} onValueChange={onBranchChange}>
            <SelectTrigger className="h-9 w-44" data-testid="bv-branch"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">Tüm şubeler</SelectItem>
              {(data.available_branches || []).map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={String(committee)} onValueChange={(v) => setCommittee(v === "all" ? "all" : Number(v))}>
          <SelectTrigger className="h-9 w-48" data-testid="bv-committee"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">Tüm komiteler</SelectItem>
            {(data.committees || []).map((c) => (
              <SelectItem key={c.committee_no} value={String(c.committee_no)}>
                #{c.committee_no} {c.name} ({c.count})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isGeneralManager(user) ? (
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-9 w-36" data-testid="bv-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm durumlar</SelectItem>
              <SelectItem value="faal">FAAL</SelectItem>
              <SelectItem value="aski">ASKI</SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <span className="h-9 px-3 inline-flex items-center rounded-md border border-border text-sm font-medium text-muted-foreground" data-testid="bv-status-fixed">FAAL</span>
        )}
        <Select value={prediction} onValueChange={setPrediction}>
          <SelectTrigger className="h-9 w-44" data-testid="bv-prediction"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm tahminler</SelectItem>
            <SelectItem value="KESIN">KESİN</SelectItem>
            <SelectItem value="BELKI">BELKİ</SelectItem>
            <SelectItem value="GELMEYECEK">GELMEYECEK</SelectItem>
            <SelectItem value="ULASILAMADI">ULAŞILAMADI</SelectItem>
            <SelectItem value="KARSI">KARŞI</SelectItem>
            <SelectItem value="BOS">İşaretsiz</SelectItem>
          </SelectContent>
        </Select>
        <Select value={responsible} onValueChange={setResponsible}>
          <SelectTrigger className="h-9 w-44" data-testid="bv-responsible"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">Tüm sorumlular</SelectItem>
            {(data.responsibles || []).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={proxy} onValueChange={setProxy}>
          <SelectTrigger className="h-9 w-40" data-testid="bv-proxy"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Belge (tümü)</SelectItem>
            <SelectItem value="yes">Belgesi alınan</SelectItem>
            <SelectItem value="no">Belgesi olmayan</SelectItem>
          </SelectContent>
        </Select>
        <Select value={travel} onValueChange={setTravel}>
          <SelectTrigger className="h-9 w-48" data-testid="bv-travel-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Ulaşım (tümü)</SelectItem>
            <SelectItem value="planned">Ulaşım tarihi girilmiş</SelectItem>
            <SelectItem value="missing">Ulaşım tarihi girilmemiş</SelectItem>
          </SelectContent>
        </Select>
        <Select value={conflict} onValueChange={setConflict}>
          <SelectTrigger className="h-9 w-56" data-testid="bv-conflict"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Katman farkı (tümü)</SelectItem>
            <SelectItem value="status">Yalnız durumu farklı olanlar</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Firma, sorumlu veya yetkili ara..." value={q}
            onChange={(e) => setQ(e.target.value)} data-testid="bv-search" />
        </div>
        <Button variant="outline" size="sm" className="h-9" disabled={exporting || !rows.length} onClick={() => doExport("xlsx")} data-testid="bv-export-xlsx">
          <Download className="h-4 w-4 mr-1.5" />Excel
        </Button>
        <Button variant="outline" size="sm" className="h-9" disabled={exporting || !rows.length} onClick={() => doExport("pdf")} data-testid="bv-export-pdf">
          <FileText className="h-4 w-4 mr-1.5" />PDF
        </Button>
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="bv-table">
          <thead className="text-muted-foreground border-b border-border bg-muted/40">
            <tr>
              <bvT.Th sortKey="bv-firma" get={(r) => r.company_name} text className="text-left font-medium py-2 px-3">Firma</bvT.Th>
              <bvT.Th sortKey="bv-komite" get={(r) => r.committee_no} className="text-left font-medium py-2 px-3">Komite</bvT.Th>
              <bvT.Th sortKey="bv-sube" get={(r) => r.branch} text className="text-left font-medium py-2 px-3">Şube</bvT.Th>
              <bvT.Th sortKey="bv-durum" get={(r) => r.eff_status} text className="text-left font-medium py-2 px-3">Durum</bvT.Th>
              <bvT.Th sortKey="bv-sube-tahmin" get={(r) => r.pred} text className="text-left font-medium py-2 px-3">Şube Tahmini</bvT.Th>
              <bvT.Th sortKey="bv-tahmin" get={(r) => r.central_pred} text className="text-left font-medium py-2 px-3">Merkez</bvT.Th>
              <bvT.Th sortKey="bv-belge" get={(r) => (r.eff_proxy ? 1 : 0)} className="text-left font-medium py-2 px-3">Belge</bvT.Th>
              <bvT.Th sortKey="bv-sorumlu" get={(r) => r.eff_responsible} text className="text-left font-medium py-2 px-3">Sorumlu</bvT.Th>
              <bvT.Th sortKey="bv-yetkili" get={(r) => r.voting_person} text className="text-left font-medium py-2 px-3">Oy Kullanacak</bvT.Th>
              <bvT.Th sortKey="bv-gelis" get={(r) => r.arrival_date} text className="text-left font-medium py-2 px-3">Geliş</bvT.Th>
              <bvT.Th sortKey="bv-donus" get={(r) => r.return_date} text className="text-left font-medium py-2 px-3">Dönüş</bvT.Th>
            </tr>
          </thead>
          <tbody>
            {bvT.sorted.slice(0, 400).map((r) => (
              <tr key={r.id} className="border-b border-border/40 hover:bg-muted/20" data-testid={`bv-row-${r.id}`}>
                <td className="py-2 px-3 font-medium max-w-[280px] truncate" title={r.company_name}>{r.company_name}</td>
                <td className="py-2 px-3 text-muted-foreground whitespace-nowrap">#{r.committee_no}</td>
                <td className="py-2 px-3 text-muted-foreground">{r.branch}</td>
                <td className="py-2 px-3 text-muted-foreground">
                  {r.eff_status === "faal" ? "FAAL" : "ASKI"}
                  {r.status_conflict && (
                    <span className="ml-1.5 inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300"
                      title={`Merkez kaydı: ${r.status === "faal" ? "FAAL" : "ASKI"} · Şube katmanı: ${r.eff_status === "faal" ? "FAAL" : "ASKI"}`}
                      data-testid={`bv-conflict-${r.id}`}>katman farkı</span>
                  )}
                </td>
                <td className="py-2 px-3">
                  {r.pred
                    ? <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${PRED_CLS[r.pred] || "bg-muted"}`}>{r.pred}</span>
                    : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="py-2 px-3">
                  {r.central_pred
                    ? <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${PRED_CLS[r.central_pred] || "bg-muted"}`}>{r.central_pred}</span>
                    : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="py-2 px-3">{r.eff_proxy ? <span className="text-emerald-600 font-medium">✓</span> : <span className="text-muted-foreground">—</span>}</td>
                <td className="py-2 px-3">{r.eff_responsible || <span className="text-muted-foreground">—</span>}</td>
                <td className="py-2 px-3">{r.voting_person || <span className="text-muted-foreground">—</span>}</td>
                <td className="py-2 px-3">
                  <Input type="date" defaultValue={r.arrival_date} disabled={saving === r.id + "arrival_date"}
                    onChange={(e) => saveDate(r, "arrival_date", e.target.value)}
                    className="h-7 w-[140px] text-xs" data-testid={`bv-arrival-${r.id}`} />
                </td>
                <td className="py-2 px-3">
                  <Input type="date" defaultValue={r.return_date} disabled={saving === r.id + "return_date"}
                    onChange={(e) => saveDate(r, "return_date", e.target.value)}
                    className="h-7 w-[140px] text-xs" data-testid={`bv-return-${r.id}`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <p className="p-8 text-center text-sm text-muted-foreground" data-testid="bv-empty">Kayıt bulunamadı</p>}
        {rows.length > 400 && (
          <p className="p-3 text-center text-xs text-muted-foreground" data-testid="bv-more">
            İlk 400 firma gösteriliyor ({rows.length} sonuç) — tam listeyi Excel/PDF olarak indirin
          </p>
        )}
      </Card>
    </div>
  );
};

export default BranchVoters;
